import React, { useRef, useEffect, useCallback } from "react";
import { FaListUl, FaBold, FaItalic } from "react-icons/fa";

const FORMAT_REGEX = /\/\*([^\n]+?)\*\/|\*\*([^\n]+?)\*\*|\*([^\n*]+?)\*/g;

function escapeHtml(str) {
  return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function getMatches(raw) {
  const matches = [];
  if (!raw) return matches;
  let m;
  FORMAT_REGEX.lastIndex = 0;
  while ((m = FORMAT_REGEX.exec(raw)) !== null) {
    if (m[1] !== undefined) matches.push({ start: m.index, end: m.index + m[0].length, texto: m[1], type: "topico" });
    else if (m[2] !== undefined) matches.push({ start: m.index, end: m.index + m[0].length, texto: m[2], type: "bold" });
    else matches.push({ start: m.index, end: m.index + m[0].length, texto: m[3], type: "italic" });
  }
  return matches;
}

// Garante que todo tópico comece no início de uma linha e termine com quebra depois.
// Retorna { raw, caret } ajustados.
function normalizarFronteirasTopicos(raw, caret) {
  const matches = getMatches(raw).filter(m => m.type === "topico");
  if (!matches.length) return { raw, caret };

  let resultado = "";
  let cursor = 0;
  let novoCaret = caret;
  let deslocamento = 0;

  for (const mt of matches) {
    resultado += raw.slice(cursor, mt.start);

    // Precisa de \n ANTES do tópico?
    const precisaQuebraAntes = mt.start > 0 && raw[mt.start - 1] !== "\n";
    if (precisaQuebraAntes) {
      resultado += "\n";
      if (caret != null && caret >= mt.start) deslocamento += 1;
    }

    resultado += raw.slice(mt.start, mt.end);

    // Precisa de \n DEPOIS do tópico?
    const proximo = raw[mt.end];
    const precisaQuebraDepois = proximo !== undefined && proximo !== "\n";
    if (precisaQuebraDepois) {
      resultado += "\n";
      if (caret != null && caret >= mt.end) deslocamento += 1;
    }

    cursor = mt.end;
  }
  resultado += raw.slice(cursor);

  if (caret != null) novoCaret = caret + deslocamento;
  return { raw: resultado, caret: novoCaret };
}

// Verifica se a posição está DENTRO de um tópico (entre /* e */)
function posicaoDentroDeTopico(raw, pos) {
  return getMatches(raw).some(m => m.type === "topico" && pos > m.start && pos < m.end);
}

const MARKERS = {
  topico: { prefix: "/*", suffix: "*/", itemClass: "topico-item", markerClass: "topico-slash" },
  bold:   { prefix: "**", suffix: "**", itemClass: "fmt-bold",    markerClass: "fmt-marker" },
  italic: { prefix: "*",  suffix: "*",  itemClass: "fmt-italic",  markerClass: "fmt-marker" },
};

function renderFormattedHTML(raw) {
  if (!raw) return "";
  const matches = getMatches(raw);
  if (!matches.length) return escapeHtml(raw);

  let html = "";
  let cursor = 0;
  for (const mt of matches) {
    const { prefix, suffix, itemClass, markerClass } = MARKERS[mt.type];
    html += escapeHtml(raw.slice(cursor, mt.start));
    html +=
      `<span class="fmt-inline ${itemClass}">` +
        `<span class="${markerClass}">${prefix}</span>` +
        escapeHtml(mt.texto) +
        `<span class="${markerClass}">${suffix}</span>` +
      `</span>`;
    cursor = mt.end;
  }
  html += escapeHtml(raw.slice(cursor));
  return html;
}

function ensureTrailingTextNodes(el) {
  const spans = el.querySelectorAll(":scope > span.fmt-inline");
  spans.forEach(span => {
    const next = span.nextSibling;
    if (!next || next.nodeType !== Node.TEXT_NODE) {
      span.parentNode.insertBefore(document.createTextNode(""), span.nextSibling);
    }
  });
}

function getSelectionOffsets(el) {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);
  if (!el.contains(range.startContainer) || !el.contains(range.endContainer)) return null;

  const pre1 = document.createRange();
  pre1.selectNodeContents(el);
  pre1.setEnd(range.startContainer, range.startOffset);
  const start = pre1.toString().length;

  const pre2 = document.createRange();
  pre2.selectNodeContents(el);
  pre2.setEnd(range.endContainer, range.endOffset);
  const end = pre2.toString().length;

  return { start, end };
}

function getCaretOffset(el) {
  const off = getSelectionOffsets(el);
  return off ? off.start : null;
}

function setCaretOffset(el, offset) {
  if (offset == null) return;
  const sel = window.getSelection();
  const range = document.createRange();

  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
  const textNodes = [];
  let n;
  while ((n = walker.nextNode())) textNodes.push(n);

  let remaining = offset;
  let chosenNode = null;
  let localOffset = 0;

  for (let i = 0; i < textNodes.length; i++) {
    const len = textNodes[i].textContent.length;
    if (remaining < len) {
      chosenNode = textNodes[i];
      localOffset = remaining;
      break;
    }
    if (remaining === len) {
      const next = textNodes[i + 1];
      if (next) { chosenNode = next; localOffset = 0; }
      else { chosenNode = textNodes[i]; localOffset = len; }
      break;
    }
    remaining -= len;
  }

  if (!chosenNode) {
    range.selectNodeContents(el);
    range.collapse(false);
  } else {
    range.setStart(chosenNode, localOffset);
    range.collapse(true);
  }
  sel.removeAllRanges();
  sel.addRange(range);
}

const HISTORY_MERGE_MS = 600;

export default function AtaTextoEditor({ value, onChange, placeholder, className, style, disabled }) {
  const editorRef = useRef(null);
  const isComposingRef = useRef(false);
  const lastSelectionRef = useRef(null);
  const historyRef = useRef({ stack: [{ raw: value || "", caret: null }], index: 0 });
  const lastPushTimeRef = useRef(0);
  const rawAnteriorRef = useRef(value || "");

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (el.textContent === (value || "")) return;
    const { raw } = normalizarFronteirasTopicos(value || "", null);
    el.innerHTML = renderFormattedHTML(raw);
    ensureTrailingTextNodes(el);
    historyRef.current = { stack: [{ raw, caret: null }], index: 0 };
  }, [value]);

  useEffect(() => {
    const handleSelectionChange = () => {
      const el = editorRef.current;
      if (!el || document.activeElement !== el) return;
      const offsets = getSelectionOffsets(el);
      if (offsets) lastSelectionRef.current = offsets;
    };
    document.addEventListener("selectionchange", handleSelectionChange);
    return () => document.removeEventListener("selectionchange", handleSelectionChange);
  }, []);

  const applyState = useCallback((state) => {
    const el = editorRef.current;
    if (!el) return;
    el.innerHTML = renderFormattedHTML(state.raw);
    ensureTrailingTextNodes(el);
    setCaretOffset(el, state.caret);
    onChange(state.raw);
  }, [onChange]);

  const syncFromRaw = useCallback((novoRaw, caret, forceCheckpoint = false) => {
    applyState({ raw: novoRaw, caret });

    const h = historyRef.current;
    const now = Date.now();
    const podeMesclar = !forceCheckpoint && (now - lastPushTimeRef.current) < HISTORY_MERGE_MS && h.index === h.stack.length - 1;

    h.stack = h.stack.slice(0, h.index + 1);
    if (podeMesclar) {
      h.stack[h.index] = { raw: novoRaw, caret };
    } else {
      h.stack.push({ raw: novoRaw, caret });
      h.index = h.stack.length - 1;
    }
    lastPushTimeRef.current = now;
  }, [applyState]);

  const undo = useCallback(() => {
    const h = historyRef.current;
    if (h.index <= 0) return;
    h.index -= 1;
    applyState(h.stack[h.index]);
  }, [applyState]);

  const redo = useCallback(() => {
    const h = historyRef.current;
    if (h.index >= h.stack.length - 1) return;
    h.index += 1;
    applyState(h.stack[h.index]);
  }, [applyState]);

  const handleInput = useCallback(() => {
    if (disabled || isComposingRef.current) return;
    const el = editorRef.current;
    if (!el) return;

    let novoRaw = el.textContent || "";
    let caret = getCaretOffset(el);

    // 1) Normaliza fronteiras: tópico sempre em linha própria
    const norm = normalizarFronteirasTopicos(novoRaw, caret);
    novoRaw = norm.raw;
    caret = norm.caret;

    rawAnteriorRef.current = novoRaw;
    syncFromRaw(novoRaw, caret, false);
  }, [syncFromRaw, disabled]);

  const handleKeyDown = useCallback((e) => {
    if (disabled) { e.preventDefault(); return; }

    const isCtrlOrCmd = e.ctrlKey || e.metaKey;
    if (isCtrlOrCmd && e.key.toLowerCase() === "z") {
      e.preventDefault();
      if (e.shiftKey) redo(); else undo();
      return;
    }
    if (isCtrlOrCmd && e.key.toLowerCase() === "y") {
      e.preventDefault();
      redo();
      return;
    }

    // Teclas que inserem/removem caracteres
    const ehEdicao =
      (e.key.length === 1 && !isCtrlOrCmd) ||
      e.key === "Backspace" ||
      e.key === "Delete";

    if (ehEdicao) {
      const el = editorRef.current;
      const raw = el.textContent || "";
      const offsets = getSelectionOffsets(el);

      if (offsets && offsets.start === offsets.end) {
        const pos = offsets.start;
        const topicos = getMatches(raw).filter(m => m.type === "topico");

        // Caret exatamente colado na borda do tópico (antes do /* ou depois do */)
        const colNoInicio = topicos.find(m => pos === m.start);
        const colNoFim    = topicos.find(m => pos === m.end);

        // Backspace na borda inicial do tópico: apaga o \n anterior, não o tópico
        // Delete na borda final do tópico: idem
        if (e.key === "Backspace" && colNoInicio && raw[pos - 1] !== "\n") {
          // não deveria ocorrer por causa da normalização, mas protege
          e.preventDefault();
          return;
        }

        // Digitando exatamente na borda: joga o caractere para uma nova linha fora do tópico
        if (e.key.length === 1 && !isCtrlOrCmd && (colNoInicio || colNoFim)) {
          e.preventDefault();
          const caractere = e.key;
          let novoRaw, novoCaret;

          if (colNoInicio) {
            // Insere ANTES do tópico, numa linha acima
            novoRaw = raw.slice(0, pos) + caractere + "\n" + raw.slice(pos);
            novoCaret = pos + 1;
          } else {
            // Insere DEPOIS do tópico, numa linha abaixo
            const temQuebra = raw[pos] === "\n";
            novoRaw = temQuebra
              ? raw.slice(0, pos + 1) + caractere + raw.slice(pos + 1)
              : raw.slice(0, pos) + "\n" + caractere + raw.slice(pos);
            novoCaret = temQuebra ? pos + 2 : pos + 2;
          }
          syncFromRaw(novoRaw, novoCaret, true);
          return;
        }
      }
    }

    if (e.key !== "Enter") return;
    e.preventDefault();

    const el = editorRef.current;
    if (!el) return;

    const raw = el.textContent || "";
    const offsets = getSelectionOffsets(el);
    const start = offsets ? offsets.start : raw.length;
    const end = offsets ? offsets.end : raw.length;

    // Cursor dentro de um tópico fechado: Enter pula pro fim dele, nunca o quebra.
    const topicoAtual = getMatches(raw).find(
      m => m.type === "topico" && start > m.start && start < m.end
    );
    const pontoCorte = topicoAtual ? topicoAtual.end : start;
    const pontoFim = topicoAtual ? topicoAtual.end : end;

    const novoRaw = raw.slice(0, pontoCorte) + "\n" + raw.slice(pontoFim);
    syncFromRaw(novoRaw, pontoCorte + 1, true);
  }, [disabled, syncFromRaw, undo, redo]);

  const applyFormat = useCallback((type) => {
    if (disabled) return;
    const el = editorRef.current;
    if (!el) return;
    const sel = lastSelectionRef.current;
    if (!sel || sel.start === sel.end) return;

    const { prefix, suffix } = MARKERS[type];
    const { start, end } = sel;
    const raw = el.textContent || "";

    const beforeSlice = raw.slice(Math.max(0, start - prefix.length), start);
    const afterSlice = raw.slice(end, end + suffix.length);

    let novoRaw, novoCaret;
    if (beforeSlice === prefix && afterSlice === suffix) {
      novoRaw = raw.slice(0, start - prefix.length) + raw.slice(start, end) + raw.slice(end + suffix.length);
      novoCaret = end - prefix.length;
    } else {
      novoRaw = raw.slice(0, start) + prefix + raw.slice(start, end) + suffix + raw.slice(end);
      novoCaret = end + prefix.length + suffix.length;
    }

    const norm = normalizarFronteirasTopicos(novoRaw, novoCaret);
    syncFromRaw(norm.raw, norm.caret, true);
  }, [disabled, syncFromRaw]);

  return (
    <div className={`ata-texto-editor-wrap${!disabled ? " editando" : ""}`}>
      {!disabled && (
        <div className="ata-texto-toolbar">
          <button type="button" onMouseDown={e => { e.preventDefault(); applyFormat("topico"); }} title="Item">
            <FaListUl size={8} />
          </button>
          <button type="button" onMouseDown={e => { e.preventDefault(); applyFormat("bold"); }} title="Negrito">
            <FaBold size={8} />
          </button>
          <button type="button" onMouseDown={e => { e.preventDefault(); applyFormat("italic"); }} title="Itálico">
            <FaItalic size={8} />
          </button>
        </div>
      )}
      <div
        ref={editorRef}
        className={`ata-texto-editor ${className || ""}`}
        style={style}
        contentEditable={!disabled}
        suppressContentEditableWarning
        onInput={handleInput}
        onCompositionStart={() => { if (!disabled) isComposingRef.current = true; }}
        onCompositionEnd={handleInput}
        onPaste={(e) => { if (disabled) e.preventDefault(); }}
        onKeyDown={handleKeyDown}
        data-placeholder={placeholder}
      />
    </div>
  );
}